import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState } from 'react';
import { FirebaseAppContext } from 'libs.firebase_react';
import { LocaleContext, Spinner } from 'libs.nucleus';
import { COLORS_HEX, ReportingChart } from 'libs.react';
import { AuthContext } from 'libs.react_internal';
import { DrilldownModal } from './drilldown_modal.component';
import { resolveChartFieldDisplayName } from '../../../../charts/utils';
import { DataReportsContext } from '../../../../contexts/data_reports/data_reports.context';
import { ReportAnalyticsEvents } from '../../../constants/report_analytics_events.constants';
import { buildChartLegendOptions } from '../../../utils/chart_legend.utils';
import { formatCubeValue, isNumericCubeFormat } from '../../../utils/cube_format.utils';
import { extractDataByX, filterAndKeepKeys, isConditionalStyle, } from '../../../utils/data_reports.utils';
import { combineSeriesFromResultSets } from '../../../utils/pagination_pivot.utils';
import { getDynamicStyles } from '../../../utils/style_helpers.utils';
import { getFormattedNumber } from '../../../utils/utils';
export const DynamicChartWrapper = ({ resultSets, pivotConfig, chartType, style: conditionalStyle, isLoading, tooltipConfig, bubbleConfig, showAnnotations = false, disablePreAggs, palette, xTitle, yTitle, builderChartType, showLabels, showLegend, showArea, showDots, xAxis, yAxis, splitBy, dotSizeMapping, dotSizeField, dotSizeConstant, normalizeBubbleSize, bubbleSizeMinOverride, bubbleSizeMaxOverride, }) => {
    const locale = useContext(LocaleContext).toString();
    const resolveChartColor = (value) => {
        if (Array.isArray(value)) {
            return value.map((item) => COLORS_HEX[item] || item);
        }
        if (!value) {
            return undefined;
        }
        return COLORS_HEX[value] || value;
    };
    const getPaletteColor = (index) => {
        const paletteColors = {
            neutral: ['neutral'],
            categorical: [
                'red',
                'redDark',
                'orange',
                'orangeDark',
                'yellow',
                'green',
                'blue',
                'blueDark',
                'purple',
                'purpleDark',
            ],
            purple: ['purple1', 'purple2', 'purple3', 'purple4', 'purple5'],
            greyscale: ['neutral', 'lightGrey', 'grey', 'darkGrey', 'black'],
        };
        const paletteKey = palette || 'categorical';
        const selectedPalette = paletteColors[paletteKey] || paletteColors.categorical;
        const colorKey = selectedPalette[index % selectedPalette.length];
        return COLORS_HEX[colorKey] || colorKey;
    };
    // `applyValueFormatting` is true only for new-flow reports (`rid`). Legacy reports skip Cube value
    // formatting in tooltips and axis ticks, falling back to the pre-SSP-3918/-4134 numeric rendering.
    const { drilldownLevel, setDrilldownLevel, studyId, applyValueFormatting } = useContext(DataReportsContext);
    const { logEvent } = useContext(FirebaseAppContext);
    const { entityId, user } = useContext(AuthContext);
    const [drillDownQuery, setDrillDownQuery] = useState({
        label: '',
        query: undefined,
        chartType: undefined,
        showAnnotations: undefined,
    });
    // Helper function to detect and format datetime strings
    const formatDateTimeLabel = (value) => {
        if (typeof value !== 'string') {
            return String(value);
        }
        // Get time dimension info from annotations
        const annotations = firstResultSet?.annotation();
        const timeDimensions = annotations?.timeDimensions;
        const dimensions = annotations?.dimensions;
        if (!timeDimensions && !dimensions) {
            return value;
        }
        // Find a time dimension with type: "time" - check both timeDimensions and dimensions
        let timeDimension = null;
        let granularity;
        // First, check timeDimensions (these usually have granularity info)
        if (timeDimensions) {
            timeDimension = Object.values(timeDimensions).find((td) => td?.type === 'time');
            granularity = timeDimension?.granularity?.name;
        }
        // If not found in timeDimensions, check regular dimensions
        if (!timeDimension && dimensions) {
            timeDimension = Object.values(dimensions).find((td) => td?.type === 'time');
            // Regular dimensions typically don't have granularity, so we'll use a default
        }
        if (!timeDimension) {
            return value;
        }
        // Helper function to implement smart year labeling for any granularity
        const getSmartFormattedLabel = (value, formatCallback) => {
            const date = new Date(value);
            const currentYear = new Date().getFullYear();
            const dataYears = new Set(vizChartPivotData
                .map((row) => {
                try {
                    return new Date(row.x).getFullYear();
                }
                catch {
                    return null;
                }
            })
                .filter((year) => year !== null));
            const hasMultipleYears = dataYears.size > 1 || !dataYears.has(currentYear);
            if (!hasMultipleYears) {
                // Single year data - use base format
                return formatCallback(date);
            }
            // Multiple years - determine if we should show year for this specific label
            const currentIndex = vizChartPivotData.findIndex((row) => row.x === value);
            const currentLabelYear = date.getFullYear();
            // Always show year on first label
            if (currentIndex === 0) {
                const baseFormat = formatCallback(date);
                return Array.isArray(baseFormat)
                    ? [...baseFormat, currentLabelYear.toString()]
                    : [baseFormat, currentLabelYear.toString()];
            }
            // Show year if it's different from the previous label's year
            const previousRowDate = new Date(vizChartPivotData[currentIndex - 1].x);
            const previousYear = previousRowDate.getFullYear();
            if (currentLabelYear !== previousYear) {
                const baseFormat = formatCallback(date);
                return Array.isArray(baseFormat)
                    ? [...baseFormat, currentLabelYear.toString()]
                    : [baseFormat, currentLabelYear.toString()];
            }
            // Same year as previous - use base format
            return formatCallback(date);
        };
        // Format based on granularity
        try {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
                return value;
            }
            switch (granularity) {
                case 'day':
                    return getSmartFormattedLabel(value, (date) => new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(date));
                case 'week':
                    // For week granularity, show week start date
                    return getSmartFormattedLabel(value, (date) => new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(date));
                case 'month':
                    // For month granularity, use smart year labeling
                    return getSmartFormattedLabel(value, (date) => new Intl.DateTimeFormat(locale, { month: 'short' }).format(date));
                case 'year':
                    // For year granularity, show just the year
                    return new Intl.DateTimeFormat(locale, {
                        year: 'numeric',
                    }).format(date);
                case 'hour':
                    // For hour granularity, show date and time with smart year labeling
                    return getSmartFormattedLabel(value, (date) => new Intl.DateTimeFormat(locale, {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                    }).format(date));
                case undefined:
                    // When used as a regular dimension (no granularity), use smart year labeling
                    // This handles cases where the field is in dimensions rather than timeDimensions
                    return getSmartFormattedLabel(value, (date) => new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(date));
                default:
                    // Default to date formatting with smart year labeling for unknown granularities
                    return getSmartFormattedLabel(value, (date) => new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(date));
            }
        }
        catch (error) {
            console.warn('Error formatting datetime label:', error);
            return value;
        }
    };
    if (isLoading || !resultSets || resultSets.length === 0) {
        return (_jsx("div", { className: 'flex items-center justify-center size-full', children: _jsx(Spinner, { size: 'xl' }) }));
    }
    // Use first ResultSet for metadata/structure
    const firstResultSet = resultSets[0];
    // For bubble charts, we need to handle data differently since chartPivot might not work as expected
    const { filteredOut: vizChartPivotData } = chartType === 'bubble'
        ? { filteredOut: resultSets.flatMap((rs) => rs?.rawData() || []), keptOnly: [] }
        : filterAndKeepKeys(resultSets.flatMap((rs) => rs?.chartPivot(pivotConfig) || []), tooltipConfig ?? []);
    const allCombinedSeries = combineSeriesFromResultSets(resultSets, pivotConfig);
    const vizSeriesData = allCombinedSeries.filter((obj) => !tooltipConfig?.includes(obj.key));
    const tooltipSeriesData = allCombinedSeries.filter((obj) => tooltipConfig?.includes(obj.key));
    const isPieType = chartType === 'pie' || chartType === 'doughnut';
    const pieData = isPieType
        ? (() => {
            const labels = vizChartPivotData.map((row) => formatDateTimeLabel(row.x));
            const firstSeries = vizSeriesData[0];
            const values = firstSeries?.series.map(({ value }) => value) || [];
            const colors = values.map((_, idx) => getPaletteColor(idx));
            return {
                labels,
                datasets: [
                    {
                        label: firstSeries?.shortTitle || '',
                        data: values,
                        backgroundColor: colors,
                    },
                ],
            };
        })()
        : null;
    // SSP-3876: scatter (and "bubble" — scatter + variable dot size) renders from
    // raw rows when xAxis/yAxis are configured. Each row maps to one point with
    // (xAxis, yAxis) as coordinates and dotSizeField as the radius in variable
    // mode. Rows are grouped by splitBy so each splitBy value gets its own colour.
    const isScatterFromBuilder = chartType === 'scatter' && !!xAxis?.key && !!yAxis?.key;
    const isBubbleVariable = isScatterFromBuilder && dotSizeMapping === 'variable' && !!dotSizeField?.key;
    const scatterBaseRadius = !isBubbleVariable && dotSizeConstant != null ? dotSizeConstant : 8;
    const MIN_NORMALISED_RADIUS = 4;
    const MAX_NORMALISED_RADIUS = 40;
    const scatterData = !isScatterFromBuilder
        ? chartType === 'scatter'
            ? {
                // Legacy fallback for scatter charts saved before SSP-3876 (no xAxis prop).
                datasets: vizSeriesData.map((item, idx) => {
                    const styles = conditionalStyle && isConditionalStyle(conditionalStyle)
                        ? { [String(conditionalStyle.property)]: getDynamicStyles(item, conditionalStyle) }
                        : Array.isArray(conditionalStyle)
                            ? getDynamicStyles(item, conditionalStyle)
                            : {};
                    const defaultColor = getPaletteColor(idx);
                    return {
                        label: item.shortTitle,
                        data: item.series.map(({ value }, pointIdx) => {
                            const xRaw = vizChartPivotData[pointIdx]?.x;
                            const xNum = Number(xRaw);
                            return { x: isNaN(xNum) ? pointIdx : xNum, y: Number(value) || 0 };
                        }),
                        ...styles,
                        backgroundColor: resolveChartColor(styles.backgroundColor) || defaultColor,
                        pointRadius: 8,
                        pointHoverRadius: 10,
                    };
                }),
            }
            : null
        : (() => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rows = resultSets.flatMap((rs) => (rs?.rawData() || []));
            let normMin = 0;
            let normMax = 0;
            if (isBubbleVariable && normalizeBubbleSize && dotSizeField?.key) {
                const allSizeValues = rows.map((row) => Number(row[dotSizeField.key])).filter((n) => !isNaN(n));
                const dataMin = allSizeValues.length > 0 ? Math.min(...allSizeValues) : 0;
                const dataMax = allSizeValues.length > 0 ? Math.max(...allSizeValues) : 0;
                normMin = bubbleSizeMinOverride ?? dataMin;
                normMax = bubbleSizeMaxOverride ?? dataMax;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const groups = new Map();
            rows.forEach((row) => {
                const key = splitBy?.key ? String(row[splitBy.key] ?? '') : '';
                const list = groups.get(key);
                if (list) {
                    list.push(row);
                }
                else {
                    groups.set(key, [row]);
                }
            });
            const datasets = Array.from(groups.entries()).map(([groupLabel, groupRows], idx) => {
                const styles = conditionalStyle && isConditionalStyle(conditionalStyle)
                    ? { [String(conditionalStyle.property)]: getDynamicStyles({ key: groupLabel }, conditionalStyle) }
                    : Array.isArray(conditionalStyle)
                        ? getDynamicStyles({ key: groupLabel }, conditionalStyle)
                        : {};
                const defaultColor = getPaletteColor(idx);
                const dataPoints = groupRows.map((row) => ({
                    x: Number(row[xAxis.key]) || 0,
                    y: Number(row[yAxis.key]) || 0,
                }));
                let pointRadii;
                let rawSizeValues;
                if (isBubbleVariable) {
                    const rawRadii = groupRows.map((row) => {
                        const num = Number(row[dotSizeField.key]);
                        return isNaN(num) || num <= 0 ? scatterBaseRadius : num;
                    });
                    rawSizeValues = rawRadii;
                    if (normalizeBubbleSize && normMax !== normMin) {
                        pointRadii = rawRadii.map((r) => {
                            const clamped = Math.max(normMin, Math.min(normMax, r));
                            return (MIN_NORMALISED_RADIUS +
                                ((clamped - normMin) / (normMax - normMin)) * (MAX_NORMALISED_RADIUS - MIN_NORMALISED_RADIUS));
                        });
                    }
                    else if (normalizeBubbleSize && normMax === normMin) {
                        pointRadii = rawRadii.map(() => (MIN_NORMALISED_RADIUS + MAX_NORMALISED_RADIUS) / 2);
                    }
                    else {
                        pointRadii = rawRadii;
                    }
                }
                else {
                    pointRadii = scatterBaseRadius;
                }
                const hoverRadii = Array.isArray(pointRadii) ? pointRadii.map((r) => r + 2) : scatterBaseRadius + 2;
                return {
                    label: groupLabel || yAxis.name || yAxis.key,
                    data: dataPoints,
                    ...styles,
                    backgroundColor: resolveChartColor(styles.backgroundColor) || defaultColor,
                    pointRadius: pointRadii,
                    pointHoverRadius: hoverRadii,
                    _rawSizeValues: rawSizeValues,
                };
            });
            return { datasets };
        })();
    const data = pieData ||
        scatterData ||
        (chartType !== 'bubble'
            ? {
                labels: vizChartPivotData.map((row) => formatDateTimeLabel(row.x)),
                datasets: vizSeriesData.map((item) => {
                    const styles = conditionalStyle && isConditionalStyle(conditionalStyle)
                        ? { [String(conditionalStyle.property)]: getDynamicStyles(item, conditionalStyle) }
                        : Array.isArray(conditionalStyle)
                            ? getDynamicStyles(item, conditionalStyle)
                            : {};
                    const defaultColor = getPaletteColor(vizSeriesData.indexOf(item));
                    const isLine = chartType === 'line';
                    return {
                        label: item.shortTitle,
                        data: item.series.map(({ value }) => value),
                        ...styles,
                        backgroundColor: resolveChartColor(styles.backgroundColor) || defaultColor,
                        borderColor: isLine
                            ? resolveChartColor(styles.borderColor) || defaultColor
                            : undefined,
                        ...(isLine && { fill: !!showArea }),
                        ...(isLine && !showDots && { pointRadius: 0 }),
                    };
                }),
            }
            : (() => {
                if (!bubbleConfig || !bubbleConfig.x || !bubbleConfig.y || !bubbleConfig.r) {
                    console.error('Missing bubbleConfig. Required: x, y, r fields');
                    return { datasets: [] };
                }
                if (!vizChartPivotData || vizChartPivotData.length === 0) {
                    return { datasets: [] };
                }
                const xKey = bubbleConfig.x;
                const yKey = bubbleConfig.y;
                const rKey = bubbleConfig.r;
                // For bubble charts with raw data, we need to find the dimension key
                // This is typically the dimension that's not one of the x, y, r measures
                const dimensionKey = Object.keys(vizChartPivotData[0]).find((key) => key !== xKey && key !== yKey && key !== rKey && !key.includes('.')) ||
                    Object.keys(vizChartPivotData[0]).find((key) => !key.includes(xKey.split('.')[1]) &&
                        !key.includes(yKey.split('.')[1]) &&
                        !key.includes(rKey.split('.')[1]));
                if (!dimensionKey) {
                    console.error('Could not find dimension key in bubble chart data');
                    return { datasets: [] };
                }
                // Get R values for normalization
                const rValues = vizChartPivotData.map((row) => parseFloat(row[rKey]) || 0).filter((val) => !isNaN(val));
                if (rValues.length === 0) {
                    return { datasets: [] };
                }
                const minValue = Math.min(...rValues);
                const maxValue = Math.max(...rValues);
                // For bubble charts, group rows by the dimension value (e.g., symptom)
                const grouped = vizChartPivotData.reduce((acc, row) => {
                    const label = row[dimensionKey];
                    if (!acc[label]) {
                        acc[label] = [];
                    }
                    acc[label].push(row);
                    return acc;
                }, {});
                // Then construct one dataset per unique label.
                const datasets = Object.keys(grouped).map((label) => {
                    const groupRows = grouped[label];
                    const styles = conditionalStyle && isConditionalStyle(conditionalStyle)
                        ? { [String(conditionalStyle.property)]: getDynamicStyles({ key: label }, conditionalStyle) }
                        : Array.isArray(conditionalStyle)
                            ? getDynamicStyles({ key: label }, conditionalStyle)
                            : {};
                    const dataPoints = groupRows.map((row) => {
                        const x = parseFloat(row[xKey]) || 0;
                        const y = parseFloat(row[yKey]) || 0;
                        const originalR = parseFloat(row[rKey]) || 0;
                        // Avoid division by zero and ensure minimum bubble size
                        const normalizedR = maxValue === minValue ? 10 : 5 + ((originalR - minValue) / (maxValue - minValue)) * 5;
                        return { x, y, r: normalizedR, originalR };
                    });
                    return {
                        label,
                        data: dataPoints,
                        ...styles,
                    };
                });
                return { datasets };
            })());
    const handleBarClick = async (_, elements) => {
        if (!elements || elements.length === 0 || drilldownLevel < 0) {
            return;
        }
        const { index, datasetIndex } = elements[0];
        // Use the original label value from vizChartPivotData for drilldown, not the formatted label
        const label = chartType !== 'bubble' && vizChartPivotData[index] ? vizChartPivotData[index].x : undefined;
        if (!label) {
            return;
        }
        let drillChartType;
        let hasDrilldownQuery = false;
        const seriesNames = firstResultSet.seriesNames(pivotConfig);
        if (!seriesNames || seriesNames.length === 0) {
            return;
        }
        // Use the clicked dataset index to get the correct yValues
        const clickedSeriesIndex = datasetIndex !== undefined ? datasetIndex : 0;
        if (!seriesNames[clickedSeriesIndex]) {
            console.error('No series found for clicked dataset index:', clickedSeriesIndex);
            return;
        }
        const clickedSeries = seriesNames[clickedSeriesIndex];
        const { yValues } = clickedSeries;
        // Try to find the measure - first try exact match, then try to find by partial match
        let measure = firstResultSet.annotation().measures[yValues[0]];
        let actualMeasureKey = yValues[0];
        if (!measure) {
            // If exact match fails, try to find a measure that matches any of the yValues
            const allMeasures = firstResultSet.annotation().measures || {};
            const availableKeys = Object.keys(allMeasures);
            // Try each yValue
            for (const yVal of yValues) {
                if (allMeasures[yVal]) {
                    measure = allMeasures[yVal];
                    actualMeasureKey = yVal;
                    break;
                }
            }
            // If still no match, try to find by suffix match (in case of cube prefixes)
            if (!measure) {
                for (const yVal of yValues) {
                    const matchingKey = availableKeys.find((key) => key.endsWith(yVal) || yVal.endsWith(key.split('.').pop() || ''));
                    if (matchingKey) {
                        measure = allMeasures[matchingKey];
                        actualMeasureKey = matchingKey;
                        break;
                    }
                }
            }
        }
        if (measure?.drillMembersGrouped?.measures) {
            if (measure?.drillMembersGrouped?.measures?.length) {
                hasDrilldownQuery = true;
            }
            drillChartType = measure?.meta?.drill_chart ?? 'table';
        }
        else if (measure?.drillMembers) {
            // Fallback to old drillMembers format
            if (measure.drillMembers.length > 0) {
                hasDrilldownQuery = true;
            }
            drillChartType = measure?.meta?.drill_chart ?? 'table';
        }
        else {
            console.log('No drill members found in measure:', measure);
        }
        if (!hasDrilldownQuery) {
            return;
        }
        // Use the dimension value from yValues as-is (e.g., "Grade 3", "Lymphadenopathy")
        const actualDimensionValue = yValues[0];
        const properYValues = [actualDimensionValue, actualMeasureKey];
        const ddQuery = {
            xValues: [label],
            yValues: properYValues,
        };
        let query;
        try {
            query = firstResultSet.drillDown(ddQuery);
        }
        catch (error) {
            console.error('Error calling drillDown:', error);
            return;
        }
        if (!query) {
            // Try alternative approach with different yValues structure
            // Try using just the measure key instead of the full yValues array
            const alternativeQuery = {
                xValues: [label],
                yValues: [actualMeasureKey],
            };
            try {
                query = firstResultSet.drillDown(alternativeQuery);
                if (query) {
                    // Override the measure to use the original drill-down measure instead of the base measure
                    query.measures = [actualMeasureKey];
                    // Add the correct filter manually for the clicked dimension value
                    // Find which dimension the clicked value belongs to by examining the original query
                    const originalDimensions = firstResultSet.loadResponse.results[0].query.dimensions;
                    // Find the dimension that is NOT the x-axis dimension (the one we clicked from)
                    // The x-axis dimension is usually dimensions[0] (e.g., the country/location dimension)
                    const xAxisDimension = originalDimensions[0]; // First dimension (usually the x-axis)
                    const clickedValueDimension = originalDimensions.find((dim) => dim !== xAxisDimension);
                    if (clickedValueDimension) {
                        // Add or update the filter for the clicked dimension value
                        const existingFilter = query.filters.find((f) => f.member === clickedValueDimension);
                        if (existingFilter) {
                            existingFilter.operator = 'equals';
                            existingFilter.values = [actualDimensionValue];
                        }
                        else {
                            query.filters.push({
                                member: clickedValueDimension,
                                operator: 'equals',
                                values: [actualDimensionValue],
                            });
                        }
                    }
                    else {
                        console.error('Could not determine clicked value dimension');
                    }
                }
            }
            catch { }
            if (!query) {
                // Try third approach: use only dimension values, excluding measure names
                const dimensionValues = [actualDimensionValue]; // Use the correct dimension value
                const thirdQuery = {
                    xValues: [label],
                    yValues: [...dimensionValues, actualMeasureKey],
                };
                try {
                    query = firstResultSet.drillDown(thirdQuery);
                }
                catch { }
                if (!query) {
                    // Try fourth approach: use the measure from drillMembersGrouped.measures
                    const drillMeasure = measure?.drillMembersGrouped?.measures?.[0];
                    if (drillMeasure) {
                        const dimensionValues = [actualDimensionValue]; // Use the correct dimension value
                        const fourthQuery = {
                            xValues: [label],
                            yValues: [...dimensionValues, drillMeasure],
                        };
                        try {
                            query = firstResultSet.drillDown(fourthQuery);
                        }
                        catch { }
                    }
                    if (!query) {
                        console.error('All drillDown attempts failed');
                        return;
                    }
                }
            }
        }
        query.filters = query.filters.filter((f) => f.values);
        // Use drilldownLevel + 1 to get the NEXT dimension in the hierarchy
        // Level 0 (first click) -> use dimensions[1] (site_number)
        // Level 1 (second click) -> use dimensions[2] (subject_number)
        const dimensionIndex = drilldownLevel + 1;
        const dimensionToUse = query.dimensions[dimensionIndex];
        if (!dimensionToUse) {
            return;
        }
        query.dimensions = [dimensionToUse];
        query.filters = query.filters.map((f) => {
            const updatedFilter = { ...f };
            if (f.values && f.values.includes('∅')) {
                updatedFilter.member = f.member;
                updatedFilter.operator = 'notSet';
            }
            return updatedFilter;
        });
        if (!query || query.dimensions.length === 0) {
            return;
        }
        setDrilldownLevel((prev) => prev + 1);
        logEvent(ReportAnalyticsEvents.DRILLDOWN_USED, {
            study_id: studyId,
            entity_id: entityId,
            account_id: user?.path ?? user?.id,
            chart_type: chartType,
            drill_level: drilldownLevel + 1,
            dimension: dimensionToUse,
        });
        // Format label for drilldown display (convert array to string if needed)
        const formattedLabel = formatDateTimeLabel(label);
        const locationLabel = Array.isArray(formattedLabel) ? formattedLabel.join(' ') : formattedLabel;
        // Create a dynamic label based on the actual clicked value
        let displayLabel;
        // Check if actualDimensionValue is a measure name (contains table.column format)
        // Extract table prefix from the actual measure key to make this dynamic
        const tablePrefix = actualMeasureKey.split('.')[0]; // e.g., "Scoring_Reactogenicity_Diary_Report"
        if (actualDimensionValue.includes(tablePrefix + '.') || actualDimensionValue === actualMeasureKey) {
            // actualDimensionValue is a measure name, just use the clicked value directly
            displayLabel = locationLabel;
        }
        else {
            // actualDimensionValue is a real dimension value (like "Grade 3")
            // Combine it with the clicked location
            displayLabel = `${actualDimensionValue} - ${locationLabel}`;
        }
        setDrillDownQuery({
            query,
            chartType: drillChartType,
            label: displayLabel, // Use formatted label for display
            showAnnotations,
            disablePreAggs,
        });
    };
    const tooltipTitleContent = (tooltipItem) => {
        // chart.js' default tooltip title is the hovered x-axis category label. This custom callback
        // replaces the default, so re-emit that label first — otherwise stacked/grouped bars drop the
        // x-axis value from the tooltip. The library preview keeps chart.js' default title (it defines
        // only a `label` callback), so emitting it here keeps the two paths identical.
        const categoryLabel = tooltipItem[0]?.label ?? '';
        const data = extractDataByX(tooltipSeriesData, tooltipItem[0].label);
        if (!data || data.length === 0) {
            return categoryLabel;
        }
        const { measures } = firstResultSet.annotation();
        // Extra tooltip series (tooltipConfig) render below the category label as "shortTitle: value".
        const extraSeriesLines = data.map((item) => {
            // Each tooltip series may carry its own Cube format (percent/abbr/etc.); apply it so the
            // extra series in the tooltip render with the same units as the main value (SSP-3918).
            // Legacy reports (no `rid`) skip Cube formatting and show the baseline numeric value.
            const cubeFormat = measures?.[item.key]?.format;
            const formattedValue = applyValueFormatting
                ? formatCubeValue(item.value, cubeFormat, locale)
                : getFormattedNumber(item.value, locale);
            return `${item.shortTitle}: ${formattedValue}`;
        });
        return [categoryLabel, ...extraSeriesLines].join('\n');
    };
    const labelContent = (tooltipItem) => {
        if (chartType === 'bubble') {
            const raw = tooltipItem.raw;
            const x = getFormattedNumber(tooltipItem.parsed.x, locale);
            const y = getFormattedNumber(tooltipItem.parsed.y, locale);
            const r = getFormattedNumber(raw.originalR, locale);
            // Get field keys from bubbleConfig or use defaults
            const xKey = bubbleConfig?.x || 'x';
            const yKey = bubbleConfig?.y || 'y';
            const rKey = bubbleConfig?.r || 'r';
            // Get human-readable labels and suffixes from resultSet annotations if available
            const xLabel = firstResultSet?.annotation()?.dimensions?.[xKey]?.shortTitle || xKey;
            const yLabel = firstResultSet?.annotation()?.measures?.[yKey]?.shortTitle || yKey;
            const rLabel = firstResultSet?.annotation()?.measures?.[rKey]?.shortTitle || rKey;
            // Get suffix (unit) from metadata
            const xSuffix = firstResultSet?.annotation()?.dimensions?.[xKey]?.meta?.suffix || '';
            const ySuffix = firstResultSet?.annotation()?.measures?.[yKey]?.meta?.suffix || '';
            const rSuffix = firstResultSet?.annotation()?.measures?.[rKey]?.meta?.suffix || '';
            return [
                `${tooltipItem.dataset.label}`,
                `${xLabel}: ${x}${xSuffix ? `${xSuffix}` : ''}`,
                `${yLabel}: ${y}${ySuffix ? `${ySuffix}` : ''}`,
                `${rLabel}: ${r}${rSuffix ? `${rSuffix}` : ''}`,
            ];
        }
        // Builder scatter / variable-size bubble (SSP-3876) has a distinct X and Y measure (and an
        // optional dot-size measure), each with its own Cube format. The generic single-measure path
        // below would format the Y value with seriesNames[0]'s format (e.g. apply the X axis percent to
        // a Y count -> "536900%"), so handle scatter explicitly and format each axis by its own field
        // (SSP-3918). The legacy `bubble` branch above is intentionally untouched.
        if (chartType === 'scatter' && xAxis?.key && yAxis?.key) {
            const annotation = firstResultSet?.annotation();
            const formatAxisValue = (axisValue, fieldKey) => {
                const cubeFormat = annotation?.measures?.[fieldKey]?.format ?? annotation?.dimensions?.[fieldKey]?.format;
                return applyValueFormatting && isNumericCubeFormat(cubeFormat)
                    ? formatCubeValue(axisValue, cubeFormat, locale)
                    : getFormattedNumber(axisValue, locale);
            };
            // Prefer Cube annotation / dataset display names when ColumnConfig.name is a bare key (SSP-4278).
            const xLabel = resolveChartFieldDisplayName({
                fieldKey: xAxis.key,
                fieldName: xAxis.name,
                annotation,
                fallback: xTitle,
            });
            const yLabel = resolveChartFieldDisplayName({
                fieldKey: yAxis.key,
                fieldName: yAxis.name,
                annotation,
                fallback: yTitle,
            });
            const lines = [
                `${tooltipItem.dataset.label}`,
                `${xLabel}: ${formatAxisValue(tooltipItem.parsed.x, xAxis.key)}`,
                `${yLabel}: ${formatAxisValue(tooltipItem.parsed.y, yAxis.key)}`,
            ];
            // Variable-size bubble: the dot radius carries the dot-size measure's value.
            if (dotSizeMapping === 'variable' && dotSizeField?.key) {
                const pointRadius = tooltipItem.dataset?.pointRadius;
                const radius = Array.isArray(pointRadius) ? pointRadius[tooltipItem.dataIndex] : pointRadius;
                if (radius !== undefined) {
                    const sizeLabel = resolveChartFieldDisplayName({
                        fieldKey: dotSizeField.key,
                        fieldName: dotSizeField.name,
                        annotation,
                    });
                    lines.push(`${sizeLabel}: ${formatAxisValue(radius, dotSizeField.key)}`);
                }
            }
            return lines;
        }
        // For pie/doughnut charts, tooltipItem.parsed is just a number, not an object with .y
        // For bar/line charts, tooltipItem.parsed is an object with .x and .y. Horizontal bars use
        // indexAxis: 'y', so the measure value lives on .x while .y is the category index.
        const isPieOrDoughnut = chartType === 'pie' || chartType === 'doughnut';
        const isHorizontal = chartType === 'horizontalBar';
        const rawValue = isPieOrDoughnut
            ? tooltipItem.parsed
            : isHorizontal
                ? tooltipItem.parsed.x
                : tooltipItem.parsed.y;
        // A measure can be a pre-formatted string (e.g. a percentage built with CONCAT(..., '%')).
        // chart.js parses that into a bare number for tooltipItem.parsed, dropping the suffix, so fall
        // back to the original string in tooltipItem.raw when it isn't purely numeric.
        const rawDatum = tooltipItem.raw;
        const isPreformatted = typeof rawDatum === 'string' && rawDatum.trim() !== '' && Number.isNaN(Number(rawDatum));
        // When value formatting is on and the measure has no Cube format, render the raw value with plain
        // locale grouping (chart.js `formattedValue`, e.g. "128,856") to match the library preview — do NOT
        // abbreviate. Legacy reports (formatting off) keep the historical compact `getFormattedNumber`.
        const value = isPreformatted
            ? rawDatum
            : applyValueFormatting
                ? tooltipItem.formattedValue
                : getFormattedNumber(rawValue, locale);
        const seriesNames = firstResultSet.seriesNames(pivotConfig);
        if (!seriesNames || seriesNames.length === 0 || !seriesNames[0]) {
            return `${tooltipItem.dataset.label}: ${value}`;
        }
        // Resolve the Cube format/suffix for the hovered dataset. Stacked and grouped bars pivot the
        // measure across a splitBy dimension, so a series' yValues is [dimensionValue, measureKey] and
        // yValues[0] is the dimension value — measures[yValues[0]] misses and the tooltip silently drops
        // the % / K-M formatting. Match the hovered dataset's series key against the measure keys by
        // suffix instead, mirroring the library-preview tooltip so both render identically (SSP-3918).
        const measures = firstResultSet.annotation().measures ?? {};
        const hoveredSeriesKey = vizSeriesData[tooltipItem.datasetIndex]?.key;
        const measureKey = hoveredSeriesKey && measures[hoveredSeriesKey]
            ? hoveredSeriesKey
            : hoveredSeriesKey
                ? Object.keys(measures).find((key) => hoveredSeriesKey.endsWith(key))
                : undefined;
        const measure = measureKey ? measures[measureKey] : null;
        const cubeFormat = measure?.format;
        // A Cube numeric format is authoritative and renders its own units (%, K/M), so apply it directly.
        // Legacy reports (no `rid`) skip it and fall through to the baseline value + suffix below.
        if (!isPreformatted && applyValueFormatting && isNumericCubeFormat(cubeFormat)) {
            return `${tooltipItem.dataset.label}: ${formatCubeValue(rawValue, cubeFormat, locale)}`;
        }
        // When the value is already a pre-formatted string it carries its own unit; don't append the
        // measure suffix on top of it (avoids e.g. "45.2%%").
        const suffix = !isPreformatted && measure?.meta?.suffix ? measure.meta.suffix : '';
        return `${tooltipItem.dataset.label}: ${value}${suffix}`;
    };
    // Helper function to build axis title from annotation
    const buildAxisTitle = (annotation, fallbackKey) => {
        if (!annotation) {
            return fallbackKey;
        }
        const label = annotation.shortTitle || annotation.title || fallbackKey;
        const suffix = annotation.meta?.suffix || '';
        return `${label}${suffix ? ` (${suffix})` : ''}`;
    };
    // Get measure metadata for axis labels if available
    const getAxisOptions = () => {
        // Cube annotations can carry `format` even when TS types don't expose it (mirrors the tooltip path).
        const formatOf = (annotation) => annotation?.format;
        // Resolve the Cube `format` for the measure(s) so axis tick labels match tooltips/table cells
        // (SSP-3972): e.g. 0.96 -> "96%", 12000 -> "12K". Only the value axis (and, for scatter/bubble,
        // the measure X axis) is formatted; category/dimension axes keep their raw labels.
        const resolveMeasureFormats = () => {
            const annotations = firstResultSet?.annotation();
            if (!annotations) {
                return {};
            }
            if (chartType === 'bubble' && bubbleConfig) {
                const xAnnotation = annotations.dimensions?.[bubbleConfig.x] || annotations.measures?.[bubbleConfig.x];
                return {
                    xAxisFormat: formatOf(xAnnotation),
                    valueAxisFormat: formatOf(annotations.measures?.[bubbleConfig.y]),
                };
            }
            if (chartType === 'scatter' && xAxis?.key && yAxis?.key) {
                const xAnnotation = annotations.measures?.[xAxis.key] || annotations.dimensions?.[xAxis.key];
                const yAnnotation = annotations.measures?.[yAxis.key] || annotations.dimensions?.[yAxis.key];
                return { xAxisFormat: formatOf(xAnnotation), valueAxisFormat: formatOf(yAnnotation) };
            }
            // Generic single-measure charts (bar/line/stacked/horizontal): find the measure key.
            const seriesNames = firstResultSet?.seriesNames(pivotConfig);
            const yValues = seriesNames?.[0]?.yValues;
            const measureKey = yValues?.find((key) => annotations.measures?.[key]) ??
                (pivotConfig?.y && pivotConfig.y.length > 0 ? pivotConfig.y[0] : undefined);
            return { valueAxisFormat: measureKey ? formatOf(annotations.measures?.[measureKey]) : undefined };
        };
        // Chart.js hands the raw numeric tick value to `callback`; format it with the measure's Cube format.
        // Legacy reports (no `rid`) leave axis ticks at the chart.js default, as before SSP-4134.
        const measureTicks = (format) => applyValueFormatting && isNumericCubeFormat(format)
            ? { ticks: { callback: (value) => formatCubeValue(value, format, locale) } }
            : {};
        // Attach tick formatters to the axis that visually carries the measure. Horizontal bars use
        // indexAxis: 'y', so their measure is on X; everything else keeps the measure on Y.
        const withMeasureTicks = (scales) => {
            const { valueAxisFormat, xAxisFormat } = resolveMeasureFormats();
            const valueAxis = chartType === 'horizontalBar' ? 'x' : 'y';
            const valueTicks = measureTicks(valueAxisFormat);
            if (Object.keys(valueTicks).length > 0) {
                scales[valueAxis] = { ...(scales[valueAxis] || {}), ...valueTicks };
            }
            const xTicks = measureTicks(xAxisFormat);
            if (Object.keys(xTicks).length > 0) {
                scales.x = { ...(scales.x || {}), ...xTicks };
            }
            return scales;
        };
        if (xTitle || yTitle) {
            const isHorizontal = chartType === 'horizontalBar';
            const isStacked = chartType === 'stackedBar' || (isHorizontal && builderChartType === 'stackedBar');
            const renderedXTitle = isHorizontal ? yTitle : xTitle;
            const renderedYTitle = isHorizontal ? xTitle : yTitle;
            return {
                scales: withMeasureTicks({
                    x: {
                        ...(isStacked ? { stacked: true } : {}),
                        title: renderedXTitle
                            ? {
                                display: true,
                                text: renderedXTitle,
                            }
                            : undefined,
                    },
                    y: {
                        ...(isStacked ? { stacked: true } : {}),
                        title: renderedYTitle
                            ? {
                                display: true,
                                text: renderedYTitle,
                            }
                            : undefined,
                    },
                }),
            };
        }
        // Return empty options if annotations are disabled
        if (!showAnnotations) {
            if (chartType === 'horizontalBar') {
                return {
                    scales: withMeasureTicks({
                        x: { stacked: builderChartType === 'stackedBar' },
                        y: { stacked: builderChartType === 'stackedBar' },
                    }),
                };
            }
            if (chartType === 'stackedBar') {
                return {
                    scales: withMeasureTicks({
                        x: { stacked: true },
                        y: { stacked: true },
                    }),
                };
            }
            const scales = withMeasureTicks({});
            return Object.keys(scales).length > 0 ? { scales } : {};
        }
        const annotations = firstResultSet?.annotation();
        if (!annotations) {
            return {};
        }
        const scales = {};
        if (chartType === 'bubble' && bubbleConfig) {
            // For bubble charts, use bubbleConfig to determine axis fields
            const xKey = bubbleConfig.x;
            const yKey = bubbleConfig.y;
            const xAnnotation = annotations.dimensions?.[xKey] || annotations.measures?.[xKey];
            const yAnnotation = annotations.measures?.[yKey];
            scales.x = {
                title: {
                    display: true,
                    text: buildAxisTitle(xAnnotation, xKey),
                },
            };
            scales.y = {
                title: {
                    display: true,
                    text: buildAxisTitle(yAnnotation, yKey),
                },
            };
        }
        else {
            // For other chart types, use seriesNames to get dimension and measure info
            const seriesNames = firstResultSet?.seriesNames(pivotConfig);
            if (seriesNames && seriesNames.length > 0 && seriesNames[0]) {
                const { xValues, yValues } = seriesNames[0];
                // Handle X-axis - for stacked charts, xValues might be undefined
                // In that case, use pivotConfig.x to get the dimension
                if (xValues && xValues.length > 0) {
                    const xKey = xValues[0];
                    const xAnnotation = annotations.dimensions?.[xKey];
                    if (xAnnotation) {
                        const xTitle = buildAxisTitle(xAnnotation, xKey);
                        scales.x = {
                            title: {
                                display: true,
                                text: xTitle,
                            },
                        };
                    }
                }
                else if (pivotConfig?.x && pivotConfig.x.length > 0) {
                    // For stacked/grouped charts, get X-axis from pivotConfig
                    const xKey = pivotConfig.x[0];
                    const xAnnotation = annotations.dimensions?.[xKey];
                    if (xAnnotation) {
                        const xTitle = buildAxisTitle(xAnnotation, xKey);
                        scales.x = {
                            title: {
                                display: true,
                                text: xTitle,
                            },
                        };
                    }
                }
                // Handle Y-axis - for stacked charts, need to find the actual measure
                // yValues might contain dimension values + measure, so find the measure
                if (yValues && yValues.length > 0) {
                    // Find the actual measure key (not dimension values)
                    const measureKey = yValues.find((key) => annotations.measures?.[key]);
                    if (measureKey) {
                        const yAnnotation = annotations.measures[measureKey];
                        if (yAnnotation) {
                            const yTitle = buildAxisTitle(yAnnotation, measureKey);
                            scales.y = {
                                title: {
                                    display: true,
                                    text: yTitle,
                                },
                            };
                        }
                    }
                    else if (pivotConfig?.y && pivotConfig.y.length > 0) {
                        // Fallback: use pivotConfig.y if available
                        const yKey = pivotConfig.y[0];
                        const yAnnotation = annotations.measures?.[yKey];
                        if (yAnnotation) {
                            const yTitle = buildAxisTitle(yAnnotation, yKey);
                            scales.y = {
                                title: {
                                    display: true,
                                    text: yTitle,
                                },
                            };
                        }
                    }
                }
            }
        }
        const shouldStackAxes = chartType === 'stackedBar' || (chartType === 'horizontalBar' && builderChartType === 'stackedBar');
        if (shouldStackAxes) {
            scales.x = {
                ...(scales.x || {}),
                stacked: true,
            };
            scales.y = {
                ...(scales.y || {}),
                stacked: true,
            };
        }
        const scalesWithTicks = withMeasureTicks(scales);
        return Object.keys(scalesWithTicks).length > 0 ? { scales: scalesWithTicks } : {};
    };
    const pieDataLabelsPlugin = {
        id: 'pieDataLabels',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        afterDraw(chart) {
            if (!chart.options?.plugins?.pieDataLabels?.enabled) {
                return;
            }
            const meta = chart.getDatasetMeta(0);
            if (!meta?.data?.length) {
                return;
            }
            const { ctx } = chart;
            meta.data.forEach((element, index) => {
                const value = chart.data.datasets[0]?.data[index];
                if (!value) {
                    return;
                }
                const { x, y } = element.getCenterPoint();
                ctx.save();
                ctx.fillStyle = 'white';
                ctx.font = 'bold 14px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(String(value), x, y);
                ctx.restore();
            });
        },
    };
    const chartPlugins = isPieType ? [pieDataLabelsPlugin] : undefined;
    return (_jsxs(_Fragment, { children: [_jsx(ReportingChart, { data: data, type: chartType, onClick: handleBarClick, plugins: chartPlugins, options: {
                    plugins: {
                        tooltip: {
                            callbacks: {
                                title: tooltipTitleContent,
                                label: labelContent,
                            },
                        },
                        legend: buildChartLegendOptions({ chartType, builderChartType, showLegend }),
                        ...{ pieDataLabels: { enabled: isPieType && !!showLabels } },
                    },
                    elements: {
                        bar: {
                            borderRadius: 8,
                        },
                    },
                    ...getAxisOptions(),
                } }), drillDownQuery.query && (_jsx(DrilldownModal, { onClose: () => setDrillDownQuery({
                    label: '',
                    query: undefined,
                    chartType: undefined,
                    showAnnotations: undefined,
                    disablePreAggs: undefined,
                }), title: `Drilldown for ${drillDownQuery.label}`, query: drillDownQuery.query, chartType: drillDownQuery?.chartType ?? 'table', showAnnotations: drillDownQuery.showAnnotations, disablePreAggs: drillDownQuery.disablePreAggs }))] }));
};
